import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Empty, PrioSpinner } from '@prio365/prio365-react-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PromanReport, PromanDocument } from '../../../../../api/promanDtos';
import {
  filterReports,
  sortReports,
  hasReportDocuments,
  formatDate,
} from '../utils/promanHelpers';

interface PromanReportListProps {
  collectionId: number;
  reports: PromanReport[];
  isLoading: boolean;
  searchQuery: string;
  organizedDocuments: Record<string, PromanDocument[]>;
  selectedReportId: number | null;
  onReportClick: (report: PromanReport) => void;
  classes: {
    loadingContainer: string;
    emptyState: string;
    reportsList: string;
    reportItem: string;
    reportItemContent: string;
    reportItemHeader: string;
    reportCode: string;
    reportDate: string;
    reportPlace: string;
    reportStatus: string;
  };
}

export const PromanReportList: React.FC<PromanReportListProps> = ({
  reports,
  isLoading,
  searchQuery,
  organizedDocuments,
  selectedReportId,
  onReportClick,
  classes,
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <PrioSpinner size="small" />
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className={classes.emptyState}>
        <Empty
          customMessage={t('projects:addIn.extensions.proman.noReports')}
        />
      </div>
    );
  }

  const filteredReports = filterReports(reports, searchQuery);
  const sortedReports = sortReports(filteredReports);

  if (filteredReports.length === 0) {
    return (
      <div className={classes.emptyState}>
        <Empty
          customMessage={t('projects:addIn.extensions.proman.noReportsFound')}
        />
      </div>
    );
  }

  return (
    <div className={classes.reportsList}>
      {sortedReports.map((report) => (
        <div
          key={report.reportId}
          className={classNames(classes.reportItem, {
            selected: selectedReportId === report.reportId,
          })}
          onClick={() => onReportClick(report)}
        >
          <div className={classes.reportItemContent}>
            <div className={classes.reportItemHeader}>
              <div className={classes.reportCode}>
                {report.reportCode} - #{report.reportId}
              </div>
              {hasReportDocuments(report, organizedDocuments) && (
                <FontAwesomeIcon icon={['fal', 'file']} />
              )}
            </div>
            {report.meetingDate && (
              <div className={classes.reportDate}>
                {formatDate(report.meetingDate)}
              </div>
            )}
            {report.meetingPlace && (
              <div className={classes.reportPlace}>{report.meetingPlace}</div>
            )}
            {report.reportStatusModel && (
              <div className={classes.reportStatus}>
                {report.reportStatusModel.statusType}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
